import React, { useState, useEffect } from 'react';
import { useTasks } from '../../hooks/useTasks';
import { useBoards } from '../../hooks/useBoards';

import {
    Plus,
    Hash,
    KanbanSquare,
    MoreHorizontal,
    Trash2
} from 'lucide-react';
import { Button, Modal, Form, Dropdown } from 'react-bootstrap';

const ProjectSidebar = ({ auth, workspaceId, onSelectView, selectedView, isMobile }) => {
    const { projects, fetchProjects, createProject, updateProject, deleteProject } = useTasks({
        token: auth?.user?.access_token,
        workspaceId
    });
    const { boards, fetchBoards, createBoard, deleteBoard } = useBoards({
        token: auth?.user?.access_token,
        workspaceId
    });

    const [showProjectModal, setShowProjectModal] = useState(false);
    const [newProjectName, setNewProjectName] = useState("");

    const [showBoardModal, setShowBoardModal] = useState(false);
    const [newBoardName, setNewBoardName] = useState("");

    useEffect(() => {
        if (workspaceId) {
            fetchProjects();
            fetchBoards();
        }
    }, [workspaceId]);

    useEffect(() => {
        // Auto-select first item if nothing selected
        if (!selectedView) {
            if (boards.length > 0) {
                onSelectView({ type: 'board', data: boards[0] });
            } else if (projects.length > 0) {
                onSelectView({ type: 'project', data: projects[0] });
            }
        }
    }, [projects, boards]);

    const handleCreateProject = async () => {
        if (!newProjectName.trim()) return;
        try {
            const newProject = await createProject({ name: newProjectName });
            setNewProjectName("");
            setShowProjectModal(false);
            onSelectView({ type: 'project', data: newProject });
        } catch (err) { }
    };

    const handleCreateBoard = async () => {
        if (!newBoardName.trim()) return;
        try {
            const newBoard = await createBoard({ name: newBoardName, config: { projects: ['all'] } }); // Default to all projects
            setNewBoardName("");
            setShowBoardModal(false);
            onSelectView({ type: 'board', data: newBoard });
        } catch (err) { }
    };

    const handleDeleteBoard = async (e, board) => {
        e.stopPropagation();
        if (window.confirm(`Delete board "${board.name}"?`)) {
            await deleteBoard(board.id);
            if (selectedView?.data?.id === board.id) onSelectView(null);
        }
    };

    return (
        <div className={`d-flex flex-column h-100 bg-light ${!isMobile ? 'border-end' : ''}`} style={isMobile ? { width: '100%' } : { width: '100%', height: '100%' }}>

            {/* BOARDS SECTION */}
            <div className="p-3 pb-0">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="m-0 fw-bold text-secondary small text-uppercase">Boards</h6>
                    <Button variant="link" size="sm" className="p-0 text-secondary" onClick={() => setShowBoardModal(true)}>
                        <Plus size={16} />
                    </Button>
                </div>
                <div className="d-flex flex-column gap-1">
                    {boards.map(b => (
                        <div
                            key={b.id}
                            onClick={() => onSelectView({ type: 'board', data: b })}
                            className={`d-flex align-items-center justify-content-between p-2 rounded cursor-pointer ${selectedView?.type === 'board' && selectedView?.data?.id === b.id ? 'bg-white shadow-sm text-primary' : 'text-dark hover-bg-gray'}`}
                            style={{ transition: 'all 0.2s', fontSize: '0.9rem' }}
                        >
                            <div className="d-flex align-items-center gap-2 text-truncate">
                                <KanbanSquare size={16} />
                                <span className="text-truncate">{b.name}</span>
                            </div>
                            {(selectedView?.type !== 'board' || selectedView?.data?.id !== b.id) && (
                                <Trash2 size={12} className="text-muted hover-text-danger opacity-50" onClick={(e) => handleDeleteBoard(e, b)} />
                            )}
                        </div>
                    ))}
                    {boards.length === 0 && <div className="text-muted small fst-italic ps-2">No boards</div>}
                </div>
            </div>

            {/* PROJECTS SECTION */}
            <div className="p-3 mt-2 border-top">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="m-0 fw-bold text-secondary small text-uppercase">Projects</h6>
                    <Button variant="link" size="sm" className="p-0 text-secondary" onClick={() => setShowProjectModal(true)}>
                        <Plus size={16} />
                    </Button>
                </div>
                <div className="d-flex flex-column gap-1 overflow-auto" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                    {projects.map(p => (
                        <div
                            key={p.id}
                            onClick={() => onSelectView({ type: 'project', data: p })}
                            className={`d-flex align-items-center gap-2 p-2 rounded cursor-pointer ${selectedView?.type === 'project' && selectedView?.data?.id === p.id ? 'bg-white shadow-sm text-primary' : 'text-dark hover-bg-gray'}`}
                            style={{ transition: 'all 0.2s', fontSize: '0.9rem' }}
                        >
                            <Hash size={16} />
                            <span className="text-truncate">{p.name}</span>
                        </div>
                    ))}
                    {projects.length === 0 && <div className="text-muted small fst-italic ps-2">No projects</div>}
                </div>
            </div>

            {/* Create Project Modal */}
            <Modal show={showProjectModal} onHide={() => setShowProjectModal(false)} centered size="sm">
                <Modal.Header closeButton><Modal.Title>New Project</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form.Control placeholder="Project Name" value={newProjectName} onChange={e => setNewProjectName(e.target.value)} autoFocus />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowProjectModal(false)}>Cancel</Button>
                    <Button variant="primary" size="sm" onClick={handleCreateProject}>Create</Button>
                </Modal.Footer>
            </Modal>

            {/* Create Board Modal */}
            <Modal show={showBoardModal} onHide={() => setShowBoardModal(false)} centered size="sm">
                <Modal.Header closeButton><Modal.Title>New Board</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form.Control placeholder="Board Name" value={newBoardName} onChange={e => setNewBoardName(e.target.value)} autoFocus />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowBoardModal(false)}>Cancel</Button>
                    <Button variant="primary" size="sm" onClick={handleCreateBoard}>Create</Button>
                </Modal.Footer>
            </Modal>

            <style>{`
        .hover-bg-gray:hover { background-color: rgba(0,0,0,0.05); }
      `}</style>
        </div>
    );
};

export default ProjectSidebar;
